import { AlertTriangle, Check, CheckIcon, ForwardIcon, Loader2, XIcon } from 'lucide-react'
import React, { useState } from 'react'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery } from 'react-query'
import axios from 'src/axios/axios'
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from 'src/components/ui/alert-dialog'
import { Button } from 'src/components/ui/button'
import { Combobox } from 'src/components/ui/combobox'
import { Label } from 'src/components/ui/label'
import { Switch } from 'src/components/ui/switch'
import { Textarea } from 'src/components/ui/textarea'
import { Tooltip, TooltipContent, TooltipTrigger } from 'src/components/ui/tooltip'
import { z } from 'zod'

type P = {
    trigger?: React.ReactNode;
    docids?: number[];
    onSuccess?: () => void
}

const ForwardBulk = ({trigger, docids, onSuccess}:P) => {
    const { t } = useTranslation()

    const [selected_college, setselected_college] = useState<undefined | string>(undefined);;
    const [remark, setremark] = useState('');
    const [selected_closeworkflow, setselected_closeworkflow] = useState(true);

    const { data:colleges, isFetching } = useQuery<ForwardUser[] | null>({
        queryKey: [`colleges`],
        queryFn: async () => {
            try {
                const { data } = await axios.get(`users/get`)
                if(data.errorcode!==0){ throw new Error(data.message) }
                if(!Array.isArray(data.object.users)){ throw new Error('users is no array') }
                
                let users = data.object.users.filter((i:ForwardUser)=>!i.blocked)
                return users
            } catch (error) {
                toast.error(t(`Collegas ophalen mislukt`), {id:'colleges'})
                return null
            }
        }
    })

    const { mutate, reset, isLoading, isError, isSuccess } = useMutation({
        mutationFn: async () => {
            if(!docids){ return; }
            toast.loading(`${t('Workflow doorsturen')}...`, {id:'forward'})
            
            const {data} = await axios.post(`wf/forward`, {
                remark: remark,
                docid: docids[0],
                userto: selected_college,
                closeworkflow: selected_closeworkflow
            })
            if(data.errorcode!==0){ throw new Error(data.message) }

            onSuccess && onSuccess()
            setremark(''); setselected_college(undefined);
            toast.success(`${t('Workflow doorgestuurd')}`, {id:'forward'})
            reset();
            return true;
        },
        onError: () => {
            toast.error(`${t('Workflow doorsturen mislukt')}`, {id:'forward'})
            setTimeout(()=>{ reset() }, 2000)
        }
    })

    function isComplete() {
        try {
            const s = z.object({
                remark: z.string().min(1)
            })
            s.parse({
                remark: remark
            })
            return true
        } catch (error) {
            return false
        }
    }
    
  return (
    <AlertDialog>
        <AlertDialogTrigger asChild>
            {trigger ? trigger : <div><Tooltip><TooltipTrigger asChild><button disabled={docids?.length === 0 || false} className='flex items-center justify-center w-10 h-10 rounded-sm min-w-10 min-h-10 hover:bg-slate-100 disabled:opacity-50'><ForwardIcon className='w-4 h-4 text-neutral-600'/></button></TooltipTrigger><TooltipContent>{t('Workflow doorsturen')}</TooltipContent></Tooltip></div>}
        </AlertDialogTrigger>
        <AlertDialogContent className='overflow-auto max-h-dvh'>
            <AlertDialogHeader>
                <AlertDialogTitle>Workflows doorsturen</AlertDialogTitle>
                <AlertDialogDescription>U staat op het punt om <span className='font-semibold'>{docids?.length} workflows</span> door te sturen.</AlertDialogDescription>
            </AlertDialogHeader>
            <div>
                <Label className='flex items-center gap-1 mb-1'>{t('Collega')}{(isFetching && !Array.isArray(colleges)) && <Loader2 className='w-3 h-3 animate-spin text-primary'/>}</Label>
                <Combobox onValueChange={(i)=>{setselected_college(i.value.toString())}} selectedValue={selected_college} options={Array.isArray(colleges) ? colleges.map((c)=>({value:c.username, label:`${c.firstname} ${c.name}`})) : []} placeholder={`${t('Selecteer een collega')}...`}/>
            </div>
            <div>
                <Label htmlFor='remark'>{t('Opmerking')}</Label>
                <Textarea value={remark} onChange={({currentTarget})=>{ setremark(currentTarget.value) }} id='remark'/>
            </div>
            <div className='flex flex-col gap-1'>
                <Label htmlFor='remark'>{t('Workflow voor mij afsluiten')}</Label>
                <Switch checked={selected_closeworkflow} onCheckedChange={(v)=>{setselected_closeworkflow(v)}}/>
            </div>
            <AlertDialogFooter>
                <AlertDialogCancel>Annuleren</AlertDialogCancel>
                <AlertDialogAction onClick={()=>{mutate()}} disabled={!selected_college || isError || isLoading} className='gap-2'>
                    {isLoading && <Loader2 className='w-4 h-4 animate-spin'/>}
                    {isError && <AlertTriangle className='w-4 h-4'/>}
                    {isSuccess && <Check className='w-4 h-4'/>}
                    {t('Doorsturen')}
                </AlertDialogAction>
            </AlertDialogFooter>
        </AlertDialogContent>
    </AlertDialog>
  )
}

export default ForwardBulk